'use client';

import CancellationRetentionMobileButton from '@/components/Form/CancellationRetention/CancellationRetentionMobileButton';
import CancellationRetentionTeaser from '@/components/Form/CancellationRetention/CancellationRetentionTeaser';
import { isCancellationForm } from '@/components/Form/CancellationRetention/cancellationRetentionHelper';
import Confirmation from '@/components/Form/Confirmation';
import FormikDebugOverlay from '@/components/Form/FormikDebugOverlay';
import HoneypotField from '@/components/Form/HoneypotField';
import {
  collectCdpFields,
  getOptionsForField,
  resolveToAllowedOption,
} from '@/components/Form/helper/cdpPrefillHelper';
import {
  type FormField,
  getInvisibleFields,
  getVisiblePageIndices,
  hasRequiredFields,
  mapServerFieldErrorsToStateKeys,
  resolveActivePageIndex,
  resolveStateKeyForValueId,
} from '@/components/Form/helper/formHelper';
import { renderFormFields } from '@/components/Form/helper/renderFormFields';
import { buildValidationSchema, initializeFormValues } from '@/components/Form/helper/schemaHelper';
import { applyServerFieldErrors } from '@/components/Form/helper/serverFieldErrors';
import { HONEYPOT_FIELD, handleSubmitFinal, showData } from '@/components/Form/helper/submitHelper';
import classes from '@/components/Form/styles/form.module.scss';
import { type FormValues } from '@/components/Form/types';
import FormProductCard from '@/components/FormProductCard/FormProductCard';
import OrderMeta from '@/components/OrderMeta/OrderMeta';
import OrderStep from '@/components/OrderStep/OrderStep';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import OrderLayoutHeader from '@/components/orderLayoutHeader/OrderLayoutHeader';
import { RichText } from '@/components/richText/RichText';
import { hasContent } from '@/helper/RichText';
import tracking, { EventCategory, type NonCommodityTransaction, type TrackingInformation } from '@/helper/tracking';
import { generateUUId } from '@/helper/utils';
import { useBankService } from '@/hooks/useBankService';
import { initGetCitiesQuery } from '@/hooks/usePostcodeService';
import { useFlashStore } from '@/providers/FlashStoreProvider';
import { useHeaderStore } from '@/providers/HeaderStoreProvider';
import OrderStepProvider from '@/providers/OrderStepContext';
import { useSettings } from '@/providers/SettingsProvider';
import { useUserStore } from '@/providers/UserStoreProvider';
import type { FormStoryblok } from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { Formik, Form as FormikForm, yupToFormErrors } from 'formik';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';

export interface FormProps {
  formTrackingId?: string;
  formSourceStoryUuid: string;
  blok: FormStoryblok;
  embedded: boolean;
  onSuccess?: () => Promise<void>;
  trackFormPurchase?: boolean;
  headline?: string;
}

const Form: FC<FormProps> = ({
  formTrackingId,
  formSourceStoryUuid,
  blok,
  embedded = true,
  onSuccess,
  trackFormPurchase = false,
  headline,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const setErrorModalOpen = useHeaderStore((state) => state.setErrorModalOpen);
  const setFlashFormData = useFlashStore((s) => s.setFormData);
  const userStore = useUserStore((state) => ({
    trackingData: state.trackingData,
    setTrackingData: state.setTrackingData,
    loggedIn: state.loggedIn,
    loggingIn: state.loggingIn,
    email: state.email,
    cdpPrefillData: state.cdpPrefillData,
  }));

  const settings = useSettings();
  const isCancellationRetentionForm = isCancellationForm(blok);
  const isNonCommodityForm = !!blok.productDataTitle || trackFormPurchase;

  const [beginCheckoutTrackingDone, setBeginCheckoutTrackingDone] = useState(false);

  /**
   * Step
   */
  const [activeStep, setActiveStep] = useState(0);
  const hasSummaryStep = blok.summary;

  /**
   *  Handle form states
   */
  const [submittedValues, setSubmittedValues] = useState<FormField[]>([]);
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);
  const [onSuccessIsFinished, setOnSuccessIsFinished] = useState(false);
  const [isSendFailure, setIsSendFailure] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Set initial form values
   */
  const searchParams = useSearchParams();
  const [isInitialValuesLoaded, setIsInitialValuesLoaded] = useState(false);
  const initialStateValues = initializeFormValues(blok, Object.fromEntries(searchParams.entries()));

  // Track current form values for dynamic validation and visible page resolution
  const [currentFormValues, setCurrentFormValues] = useState<FormValues>(initialStateValues);

  const visiblePageIndices = getVisiblePageIndices(blok, currentFormValues);
  const resolvedActiveStep = resolveActivePageIndex(visiblePageIndices, activeStep);
  const isSummaryStep = hasSummaryStep && activeStep === visiblePageIndices.length;
  const currentContentIndex = isSummaryStep
    ? (visiblePageIndices[visiblePageIndices.length - 1] ?? 0)
    : (visiblePageIndices[resolvedActiveStep] ?? visiblePageIndices[0] ?? 0);

  const steps = visiblePageIndices.map((index) => blok.content?.[index]?.name || '');
  if (hasSummaryStep) {
    steps.push(t('Zusammenfassung'));
  }
  const stepCount = steps.length;
  const isLastStep = activeStep === stepCount - 1;
  const currentStep =
    !isSummaryStep && blok.content?.[currentContentIndex] ? blok.content[currentContentIndex] : undefined;
  const title = steps[activeStep];
  const description = isSummaryStep ? t('Prüfen Sie Ihre Eingaben') : currentStep?.description;

  useEffect(() => {
    setCurrentFormValues(initialStateValues);
    setIsInitialValuesLoaded(true);
  }, [blok]);

  useEffect(() => {
    const indices = getVisiblePageIndices(blok, currentFormValues);
    const maxStep = indices.length + (blok.summary ? 1 : 0) - 1;
    if (maxStep < 0) {
      return;
    }
    setActiveStep((prev) => (prev > maxStep ? maxStep : prev));
  }, [currentFormValues, blok]);

  /**
   * Validation
   */

  // Add service for validation
  const bankService = useBankService();
  const postCode = initGetCitiesQuery();

  const formId = blok.id || blok._uid;

  const scrollToFormBeginning = () => {
    if (!embedded) {
      document.querySelector('html')!.scrollTop = 0;
    } else {
      document.querySelector(`[id="${formId}"]`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  /**
   * Navigation
   */

  // Go to previous step
  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
    scrollToFormBeginning();
  }, []);

  // Go to next step if it is valid
  const handleNext = useCallback(() => {
    setActiveStep((prev) => prev + 1);
    scrollToFormBeginning();
  }, []);

  useEffect(() => {
    if (isSuccessfullySubmitted && onSuccess && !onSuccessIsFinished) {
      onSuccess();
      setOnSuccessIsFinished(true);
    }
  }, [isSuccessfullySubmitted, onSuccess]);

  const getNonCommodityTrackingInformation = (): TrackingInformation => {
    return {
      itemId: formTrackingId ?? '',
      itemName: blok.productDataTitle ?? '',
      index: 0,
      itemCategory: EventCategory.NonCommodityPage,
      itemCategory2: blok.id,
    };
  };

  useEffect(() => {
    if (beginCheckoutTrackingDone) return;

    if (!userStore) return;

    if (!isNonCommodityForm) {
      userStore.setTrackingData(undefined);
      setBeginCheckoutTrackingDone(true);
      return;
    }

    const currentTrackingData = userStore.trackingData;
    const matchingData = currentTrackingData?.itemName === blok.productDataTitle;

    if (currentTrackingData && !matchingData) {
      userStore.setTrackingData(undefined);
      console.log(
        'Found tracking data in non-commodity form or non-matching tracking data for this form, deleting tracking data from store'
      );
    }

    if (currentTrackingData?.itemId) {
      tracking.beginCheckout([currentTrackingData]);
    } else if (!!formTrackingId) {
      const trackingInformation = getNonCommodityTrackingInformation();
      userStore.setTrackingData(trackingInformation);
      tracking.beginCheckout([trackingInformation]);
    } else {
      console.error('could not track form');
    }

    setBeginCheckoutTrackingDone(true);
  }, [userStore, beginCheckoutTrackingDone, isNonCommodityForm, formTrackingId, blok]);

  useEffect(() => {
    if (isSendFailure) {
      setErrorModalOpen(true);
    }
  }, [isSendFailure]);

  /**
   * tracks a non commodity purchase
   */
  const trackPurchase = function () {
    if (isNonCommodityForm && userStore?.trackingData?.itemId) {
      const nonCommodityTransaction: NonCommodityTransaction = {
        transactionId: generateUUId(),
      };

      tracking.nonCommodityPurchase([userStore.trackingData], nonCommodityTransaction);
    }
  };

  /**
   * Loading Form
   */
  if (!isInitialValuesLoaded || userStore?.loggingIn) {
    if (embedded) {
      return (
        <div className={classes.embeddedLoadingHost}>
          <LoadingBackdrop loading type="area" />
        </div>
      );
    }

    return <LoadingBackdrop loading type="page" />;
  }

  const isBoxed =
    !isSummaryStep &&
    !!blok.content?.[currentContentIndex] &&
    !blok.content[currentContentIndex]!.content?.map((b) => b.component).includes('FormSection');

  return (
    <>
      {!embedded && (
        <OrderLayoutHeader
          headline={title || ''}
          onClickBack={activeStep > 0 ? handleBack : undefined}
          currentStep={activeStep + 1}
          numberOfSteps={stepCount}
          showCart={false}
          meta={
            blok.showHelp && (
              <OrderMeta
                buttonText={blok.helpCtaButton}
                headline={blok.helpHeadline}
                content={hasContent(blok.helpContent) && <RichText text={blok.helpContent} />}
              />
            )
          }
        />
      )}
      {!embedded && <LoadingBackdrop loading={isLoading} text={t('Daten werden übermittelt')} type="page" />}
      <Formik
        initialValues={{ ...initialStateValues, [HONEYPOT_FIELD]: '' }}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={async (values: FormValues, { setErrors, setSubmitting, setTouched }) => {
          setTouched({});
          showData(values, blok, setSubmittedValues);

          if (isLastStep) {
            const { fieldErrors } = await handleSubmitFinal(
              values,
              blok,
              setIsLoading,
              setIsSendFailure,
              setIsSuccessfullySubmitted,
              router.push,
              setFlashFormData,
              trackPurchase,
              pathname,
              formSourceStoryUuid
            );

            if (fieldErrors) {
              const mappedFieldErrors = mapServerFieldErrorsToStateKeys(blok, fieldErrors, values);
              await applyServerFieldErrors(mappedFieldErrors, { setErrors, setTouched }, t);
            }
          } else {
            handleNext();
          }

          setSubmitting(false);
        }}
        validate={async (values) => {
          if (isSummaryStep) {
            return {};
          }

          const schema = buildValidationSchema(blok, blok.content?.[currentContentIndex]?.content, values, {
            bankService,
            postCode,
          });

          if (!schema) return {};

          try {
            await schema.validate(values, { abortEarly: false });
            return {};
          } catch (error) {
            if (error instanceof ValidationError) {
              return yupToFormErrors(error);
            }
            throw error;
          }
        }}
      >
        {({ values, errors, touched, setFieldValue, handleChange }) => {
          const showRequiredMessage = !isSummaryStep && hasRequiredFields(blok, values, currentContentIndex);

          // Sync form values for visibility/validation; compare by content to avoid infinite loops
          const valuesSnapshot = JSON.stringify(values);
          useEffect(() => {
            setCurrentFormValues(values);
            // valuesSnapshot captures content changes; values is the latest object from Formik
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [valuesSnapshot]);

          useEffect(() => {
            if (!userStore.loggedIn || searchParams.has('email')) return;

            const emailValue = userStore.email;
            if (emailValue == null || emailValue === '') return;

            const targetStateKey = resolveStateKeyForValueId(blok, 'email', values);
            const current = values[targetStateKey];
            if (current != null && current !== '') return;

            setFieldValue(targetStateKey, emailValue);
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [userStore.loggedIn, userStore.email, valuesSnapshot]);

          // CDP prefill effect must be registered unconditionally before any early returns
          useEffect(() => {
            const prefillData = userStore?.cdpPrefillData;
            const prefillEnabled = settings?.cdp_prefill_enabled;

            if (userStore?.loggedIn && prefillData && prefillEnabled) {
              // 1) Hardcoded prefill
              const fieldMap = {
                salutation: prefillData.salutation,
                email: userStore.email,
                title: prefillData.title,
                firstname: prefillData.firstname,
                lastname: prefillData.lastname,
                tel: prefillData.phone,
                phone: prefillData.phone,
                street: prefillData.street,
                streetnumber: prefillData.houseNumber,
                zip: prefillData.zip,
                city: prefillData.city,
                zip_city: prefillData.city,
                contractaccount: prefillData.vertragsKontoNummer,
                counterNumber: prefillData.zaehlernummer,
              };

              Object.entries(fieldMap)
                .filter(([field]) => !searchParams.has(field))
                .forEach(([field, value]) => {
                  let actualValue = value;

                  const options = getOptionsForField(blok, field);
                  if (options.length > 0) {
                    const resolvedValue = resolveToAllowedOption(value, options);
                    if (resolvedValue) {
                      actualValue = resolvedValue;
                    }
                  }

                  if (value != null && value !== '') {
                    setFieldValue(resolveStateKeyForValueId(blok, field, values), actualValue);
                  }
                });

              // 2) scan SB fields for cdp_field and map directly to fieldMap
              //    Use strict Storyblok types and a helper to keep this effect lean
              const fieldMapKeys = new Set(Object.keys(fieldMap));

              const collected = collectCdpFields(blok);

              collected.forEach(({ id, cdpField, type }) => {
                // Respect URL overrides
                if (searchParams.has(id)) return;

                // Do not override fields already handled by the first pass
                if (fieldMapKeys.has(id)) return;

                // Given the guarantee, cdpField is a key of fieldMap
                const key = cdpField as keyof typeof fieldMap;
                let value = fieldMap[key];

                if (type === 'FormRadioGroup' || type === 'FormSelect') {
                  const options = getOptionsForField(blok, id);
                  const resolvedValue = resolveToAllowedOption(value, options);
                  if (resolvedValue) {
                    value = resolvedValue;
                  }
                }

                if (value != null && value !== '') {
                  setFieldValue(resolveStateKeyForValueId(blok, id, values), value);
                }
              });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [userStore.cdpPrefillData, settings?.cdp_prefill_enabled]);

          if (isSendFailure) {
            return <OrderStep />;
          }
          if (isSuccessfullySubmitted && blok?.successMessage) {
            return (
              <OrderStepProvider>
                <OrderStep>
                  <RichText text={blok?.successMessage} />
                </OrderStep>
              </OrderStepProvider>
            );
          }

          return (
            <FormikForm
              {...storyblokEditable(blok)}
              id={formId}
              className={classNames('formik', classes.formik)}
              noValidate
              aria-label="Formular"
              encType="multipart/form-data"
            >
              {process.env.NEXT_PUBLIC_STORYBLOK_API_VERSION === 'draft' ? (
                <FormikDebugOverlay
                  errors={errors}
                  touched={touched}
                  validationSchema={buildValidationSchema(blok, blok.content?.[currentContentIndex]?.content, values, {
                    bankService,
                    postCode,
                  })}
                  values={values}
                />
              ) : null}
              <OrderStepProvider>
                {isCancellationRetentionForm ? (
                  <CancellationRetentionTeaser productValue={values.product} blok={blok} />
                ) : null}
                <OrderStep
                  formId={formId}
                  title={embedded ? headline : description}
                  className={classNames('fullWidth')}
                  boxed={isBoxed}
                  prev={activeStep > 0 ? handleBack : undefined}
                  next={!isLastStep && !isSuccessfullySubmitted ? () => {} : undefined}
                  nextEnabled={true}
                  action={isLastStep && !isSuccessfullySubmitted ? () => {} : undefined}
                  isFirstStep={activeStep === 0}
                  isLastStep={isLastStep}
                  actionLabel={blok.submitLabel || t('Absenden')}
                  embedded={embedded}
                  secondaryAction={
                    isCancellationRetentionForm ? (
                      <CancellationRetentionMobileButton productValue={values.product} blok={blok} />
                    ) : undefined
                  }
                  before={
                    blok.productDataTitle && (
                      <FormProductCard
                        className="fullWidth"
                        title={blok.productDataTitle}
                        subtitle={blok.productDataSubtitle}
                        contentTitle={blok.productDataContentTitle}
                        content={blok.productDataContent}
                      />
                    )
                  }
                  disableStickyButton={true}
                  isLoading={embedded ? isLoading : undefined}
                  loadingText={embedded ? t('Daten werden übermittelt') : undefined}
                  showRequiredMessage={showRequiredMessage}
                >
                  <HoneypotField
                    id={`${formId}-${HONEYPOT_FIELD}`}
                    name={HONEYPOT_FIELD}
                    label="Homepage"
                    value={(values[HONEYPOT_FIELD] as string | undefined) || ''}
                    onChange={handleChange}
                  />

                  {blok.summary && isLastStep ? (
                    <Confirmation
                      invisibleFields={getInvisibleFields(blok, values)}
                      submittedValues={submittedValues}
                    />
                  ) : (
                    <div className="formGrid">{renderFormFields(blok, values, currentContentIndex, embedded)}</div>
                  )}
                </OrderStep>
              </OrderStepProvider>
            </FormikForm>
          );
        }}
      </Formik>
    </>
  );
};

export default Form;
